import { useEffect, useMemo, useState } from "react";
import type { PreisPosition, Preise } from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";
import { Icon } from "../../shell/Icon";

/**
 * Preis-Edit-Modal im Redesign-Look. Funktional unverändert — Recompute-
 * Preview clientseitig, Save schickt PATCH zum Backend.
 */

export function PreisEditDialog({
  tenantId,
  anfrageId,
  position,
  menge,
  einheit,
  onClose,
  onSaved,
}: {
  tenantId: string;
  anfrageId: string;
  position: PreisPosition;
  menge: number;
  einheit: string;
  onClose: () => void;
  onSaved: (preise: Preise) => void;
}) {
  const [rabatt, setRabatt] = useState<string>(String(position.rabattProzent));
  const [sonderpreis, setSonderpreis] = useState<string>(
    position.sonderpreis !== null ? String(position.sonderpreis) : ""
  );
  const [marge, setMarge] = useState<string>(String(position.margeProzent));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const preview = useMemo(() => {
    const rabattN = parseFloatOrNull(rabatt) ?? 0;
    const sonderpreisN = parseFloatOrNull(sonderpreis);
    const margeN = parseFloatOrNull(marge) ?? 0;
    const listenpreis = position.listenpreis ?? 0;
    const ekPreis =
      sonderpreisN !== null ? sonderpreisN : listenpreis * (1 - rabattN / 100);
    const einzelpreis = round2(ekPreis * (1 + margeN / 100));
    const gesamtpreis = round2(einzelpreis * menge);
    return { ekPreis: round2(ekPreis), einzelpreis, gesamtpreis };
  }, [rabatt, sonderpreis, marge, position.listenpreis, menge]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const patch: {
        rabattProzent?: number;
        sonderpreis?: number;
        margeProzent?: number;
      } = {};
      const rabattN = parseFloatOrNull(rabatt);
      const sonderpreisN = parseFloatOrNull(sonderpreis);
      const margeN = parseFloatOrNull(marge);
      if (rabattN !== null) patch.rabattProzent = rabattN;
      if (sonderpreisN !== null) patch.sonderpreis = sonderpreisN;
      if (margeN !== null) patch.margeProzent = margeN;
      const updated = await api.patchPreis(
        tenantId,
        anfrageId,
        position.positionId,
        patch
      );
      onSaved(updated);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setError(body?.hint ?? body?.error ?? err.message);
      } else if (err instanceof Error) setError(err.message);
      else setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 620 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="uppercase-mini">/ PREISE / EDIT</div>
            <div className="modal-title">Preis anpassen</div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="pick-target">
            <div className="uppercase-mini" style={{ color: "var(--fg-dim)" }}>
              POSITION
            </div>
            <div className="pick-pos mono">
              {position.positionId} · {position.artikelCode ?? "—"}
            </div>
            <div className="pick-meta mono dim">
              {menge} {einheit} ·{" "}
              {position.listenpreis !== null
                ? `Listenpreis ${position.listenpreis.toFixed(2)} EUR`
                : "kein Listenpreis"}
            </div>
          </div>

          <div className="pe-grid">
            <div className="pe-cell">
              <label className="uppercase-mini">RABATT %</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={rabatt}
                onChange={(e) => setRabatt(e.currentTarget.value)}
                aria-label="Rabatt %"
                className="input mono pe-input"
              />
              <div className="uppercase-mini dim">0–100, 0 = kein Rabatt</div>
            </div>

            <div className="pe-cell">
              <label className="uppercase-mini">SONDERPREIS EUR</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={sonderpreis}
                onChange={(e) => setSonderpreis(e.currentTarget.value)}
                placeholder="(leer)"
                aria-label="Sonderpreis EUR"
                className="input mono pe-input"
              />
              <div className="uppercase-mini dim">
                leer = Rabatt anwenden; gesetzt = schlägt Rabatt
              </div>
            </div>

            <div className="pe-cell">
              <label className="uppercase-mini">MARGE %</label>
              <div className="pe-marge">
                <input
                  type="number"
                  min={0}
                  max={500}
                  step={0.1}
                  value={marge}
                  onChange={(e) => setMarge(e.currentTarget.value)}
                  aria-label="Marge %"
                  className="input mono pe-input"
                />
                <span className="pe-perc mono">%</span>
              </div>
              <div className="uppercase-mini dim">Aufschlag auf EK</div>
            </div>
          </div>

          <div className="pe-cell pe-result" style={{ marginTop: 14 }}>
            <div className="uppercase-mini">RECOMPUTE-PREVIEW</div>
            <div className="pe-vk mono">
              {preview.einzelpreis.toFixed(2)}
              <span className="pe-eur"> €</span>
              <span className="dim" style={{ fontSize: 12, marginLeft: 12 }}>
                / {einheit}
              </span>
            </div>
            <div className="uppercase-mini dim">VK aus EK + Marge</div>
          </div>

          <div className="pe-summary">
            <span>
              EK:{" "}
              <strong className="mono">{preview.ekPreis.toFixed(2)} EUR</strong>
            </span>
            <span>
              Einzelpreis:{" "}
              <strong className="mono">{preview.einzelpreis.toFixed(2)} EUR</strong>
            </span>
            <span>
              Gesamt:{" "}
              <strong
                className="mono"
                style={{ color: "var(--ok)", fontSize: 14 }}
              >
                {preview.gesamtpreis.toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                EUR
              </strong>
            </span>
          </div>

          <div
            className="warn-line"
            style={{
              marginTop: 14,
              borderLeftColor: "var(--acc)",
              background: "oklch(0.72 0.12 230 / 0.07)",
            }}
          >
            <strong>Hinweis:</strong> Override gilt bis zum nächsten
            Phase-5-Lauf. Für eine dauerhafte Anpassung die Konditionen in den
            Stammdaten pflegen.
          </div>

          {error && (
            <div
              className="warn-line"
              style={{
                marginTop: 14,
                borderLeftColor: "var(--bad)",
                background: "oklch(0.72 0.18 25 / 0.08)",
              }}
            >
              <strong>Speichern fehlgeschlagen:</strong>{" "}
              <span className="mono">{error}</span>
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Speichere …" : <><Icon name="check" size={14} /> Speichern + Recompute</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function parseFloatOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
